import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';

import type { RemoveRecordedPaymentResponse } from '../../store/api';
import { useRemoveRecordedPayment } from '../../store/hooks';
import { formatCents } from '../v2/shared';

import type { CreditSourceEntry } from './rows';
import { serverReasonOf } from './serverReason';

interface Props {
  /** PAYMENT-type sources only — see removablePaymentsOf. */
  payments: CreditSourceEntry[];
  onClose: () => void;
  onRemoved: (result: RemoveRecordedPaymentResponse) => void;
}

/**
 * Remove a payment recorded by mistake (gdn-3255).
 *
 * NOT a refund, and the copy may never blur the two. A refund sends money
 * back; this deletes a bookkeeping entry for money that never arrived —
 * a biller typed it in error. Every sentence here says "removed", never
 * "refunded" or "returned", because the practice gives nothing back and
 * telling them otherwise would invite a second, real refund.
 *
 * A picker rather than a single target: the account-credit row aggregates
 * its constituent payments, so the biller has to say which one was the
 * mistake. Only PAYMENT sources are offered; transfers, refunds and
 * reversals are history with nothing to remove.
 *
 * The server is the eligibility authority — a Stripe-sourced payment must
 * be refunded instead, unsafe allocation targets are refused — and its
 * refusals are rendered verbatim. It writes them as biller-readable
 * sentences; a paraphrase here would be the FE inventing a diagnosis it
 * does not hold.
 */
export const RemoveRecordedPaymentDialog = ({ payments, onClose, onRemoved }: Props) => {
  const { removeRecordedPayment, isLoading } = useRemoveRecordedPayment();
  const [selectedId, setSelectedId] = useState(payments[0]?.entryId ?? '');
  const [refusal, setRefusal] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const busy = submitting || isLoading;

  const selected = payments.find(p => p.entryId === selectedId) ?? null;

  const handleConfirm = async () => {
    if (busy || !selected) return;
    setSubmitting(true);
    setRefusal(null);
    try {
      const result = await removeRecordedPayment(selected.entryId);
      if (result.success) {
        onRemoved({ ...result.data, amount_cents: result.data.amount_cents ?? selected.amountCents });
        return;
      }
      setRefusal(
        serverReasonOf(result.error) ??
          'Could not remove this payment — nothing was changed.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>
        Remove recorded payment
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          For a payment entered by mistake. Charges it paid may owe again and any unused
          credit is removed — nothing is sent back.
        </Typography>
        <RadioGroup value={selectedId} onChange={event => setSelectedId(event.target.value)}>
          {payments.map(payment => (
            <FormControlLabel
              key={payment.entryId}
              value={payment.entryId}
              control={<Radio size="small" />}
              label={
                <Typography variant="body2">
                  {payment.label} — {formatCents(payment.amountCents)}
                  {payment.date && (
                    <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 0.75 }}>
                      {payment.date}
                    </Typography>
                  )}
                </Typography>
              }
            />
          ))}
        </RadioGroup>
        {refusal && (
          <Alert severity="error" sx={{ mt: 1.5, fontSize: '0.8125rem' }}>
            {refusal}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={busy || !selected}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {busy
            ? 'Removing…'
            : selected
              ? `Remove this ${formatCents(selected.amountCents)} recorded payment`
              : 'Remove payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Describe the server's result without implying a refund or inventing unused credit. */
export const removalMessage = (
  result: Pick<RemoveRecordedPaymentResponse, 'amount_cents' | 'unapplied_cents'>,
): string => {
  const removed = `Recorded ${formatCents(result.amount_cents)} payment removed.`;
  return (result.unapplied_cents ?? 0) > 0
    ? `${removed} ${formatCents(result.unapplied_cents ?? 0)} of charges it paid owe again.`
    : removed;
};
